import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, Card, Chip, CircularProgress, Typography, Switch, IconButton } from '@mui/joy';
import { AutomationsAPI } from '../../lib/apiAutomations';
import type { AutomationListItem } from '../../types/automations';
import { Trash as Delete, PencilSimple as Edit, Plus } from '@phosphor-icons/react';
import { FeatureGate } from '../../components/common/FeatureGate';
import { PageHeader } from '../../components/common/PageHeader';
import { ResponsiveTable, type ResponsiveColumn } from '../../components/common/ResponsiveTable';

const PAGE_SIZE = 20;

const AutomationsListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [actionError, setActionError] = useState('');
  const { data, isPending, isFetching, isError, refetch } = useQuery({
    queryKey: ['automations-list', currentPage],
    queryFn: () => AutomationsAPI.list(currentPage),
    retry: false,
  });
  const items = data?.results ?? [];

  const update = async (item: AutomationListItem, remove = false) => {
    if (busyId !== null) return;
    if (remove && !window.confirm(`Delete “${item.name}”? This cannot be undone.`)) return;
    setBusyId(item.id);
    setActionError('');
    try {
      if (remove) await AutomationsAPI.destroy(item.id);
      else await AutomationsAPI.update(item.id, { is_active: !item.is_active });
      if (remove && items.length === 1 && currentPage > 1) setCurrentPage(page => page - 1);
      await queryClient.invalidateQueries({ queryKey: ['automations-list'] });
    } catch {
      setActionError(`Could not ${remove ? 'delete' : 'update'} “${item.name}”. Please try again.`);
    } finally {
      setBusyId(null);
    }
  };

  const columns: ResponsiveColumn<AutomationListItem>[] = [
    { key: 'name', header: 'Automation', width: '28%', mobileVariant: 'header', render: item => <Box>
      <Typography level="title-sm">{item.name}</Typography>
      {item.description && <Typography level="body-xs" sx={{ color: 'text.secondary' }}>{item.description}</Typography>}
    </Box> },
    { key: 'active', header: 'Enabled', render: item => <Switch checked={item.is_active}
      disabled={busyId !== null || isFetching} onChange={() => update(item)} color={item.is_active ? 'success' : 'neutral'}
      slotProps={{ input: { 'aria-label': `Enable ${item.name}` } }} /> },
    { key: 'triggers', header: 'Triggers', render: item => item.triggers_count },
    { key: 'steps', header: 'Actions', render: item => item.actions_count },
    { key: 'lastRun', header: 'Last run', width: '18%', render: item => <Typography level="body-xs">{item.last_run_at ? new Date(item.last_run_at).toLocaleString() : 'Not run yet'}</Typography> },
    { key: 'status', header: 'Result', render: item => item.last_run_status ? <Chip size="sm" variant="soft"
      color={item.last_run_status === 'failed' ? 'danger' : item.last_run_status === 'partial' ? 'warning' : item.last_run_status === 'success' ? 'success' : 'primary'}>{item.last_run_status}</Chip> : '—' },
    { key: 'created', header: 'Created', render: item => <Typography level="body-xs">{item.created_at ? new Date(item.created_at).toLocaleDateString() : '—'}</Typography> },
  ];

  return <Box sx={{ p: { xs: 1.5, md: 3 }, minWidth: 0 }}>
    <PageHeader title="Automations" section="Advanced"
      description="Let ApexOS handle repeat work. Choose what starts a workflow, add its steps, then enable it when you’re ready. Review recent results here."
      actions={<Button startDecorator={<Plus />} onClick={() => navigate('/advanced/automations/new')}>New Automation</Button>} />
    {actionError && <Alert color="danger" sx={{ mb: 2 }}>{actionError}</Alert>}
    {isError && <Alert color="danger" sx={{ mb: 2 }} endDecorator={<Button size="sm" color="danger" variant="soft" onClick={() => refetch()}>Retry</Button>}>Could not load automations. Please try again.</Alert>}
    {isPending ? <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 5 }}><CircularProgress size="sm" aria-label="Loading automations" /><Typography>Loading automations…</Typography></Box> : !isError && <Box aria-busy={isFetching}>
      <ResponsiveTable columns={columns} rows={items} getRowKey={item => item.id} actionsWidth="96px" actionsHeader="Manage"
        rowActions={item => <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton size="sm" variant="plain" aria-label={`Edit ${item.name}`} onClick={() => navigate(`/advanced/automations/${item.id}`)}><Edit /></IconButton>
          <IconButton size="sm" variant="plain" color="danger" aria-label={`Delete ${item.name}`} disabled={busyId !== null || isFetching} onClick={() => update(item, true)}><Delete /></IconButton>
        </Box>}
        emptyState={<Card variant="outlined" sx={{ alignItems: 'flex-start', p: 3 }}><Typography level="title-lg">Create your first automation</Typography><Typography level="body-sm">Start with one repeated task, such as notifying your team when a new lead arrives. Add a trigger and the steps you want ApexOS to take.</Typography><Button variant="soft" sx={{ mt: 1 }} onClick={() => navigate('/advanced/automations/new')}>Create automation</Button></Card>}
        pagination={{ pageSize: PAGE_SIZE, page: currentPage, totalCount: data?.count ?? 0, onPageChange: setCurrentPage, itemNoun: 'automations' }} />
    </Box>}
  </Box>;
};

const AutomationsListPageWithGate: React.FC = () => {
  return (
    <FeatureGate
      requiredTiers={['premium', 'enterprise']}
      featureName="Workflow Automations"
      featureDescription="Create powerful automated workflows to streamline your business processes and save time on repetitive tasks."
      benefits={[
        'Custom workflow automation builder',
        'Event-driven triggers from any module',
        'Multi-step action sequences',
        'Email and SMS notification automation',
        'Conditional logic and business rules',
        'Integration with all system modules',
        'Automation performance analytics',
        'Error handling and retry logic'
      ]}
    >
      <AutomationsListPage />
    </FeatureGate>
  );
};

export default AutomationsListPageWithGate;